import type { PlayerWeekScore } from './types';
import { playerFirstName } from './roster';

interface Bar {
  label: string;
  value: number;
  color: [number, number, number];
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

export class WebGLBoard {
  private canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private buffer: WebGLBuffer | null = null;
  private particles: Particle[] = [];
  private reducedMotion = false;
  private raf = 0;
  private bars: Bar[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.init();
  }

  private init(): void {
    const gl = this.canvas.getContext('webgl2', { alpha: true, antialias: true });
    if (!gl) return;
    this.gl = gl;

    const vs = `#version 300 es
      in vec2 a_pos;
      uniform vec2 u_resolution;
      void main() {
        vec2 clip = (a_pos / u_resolution) * 2.0 - 1.0;
        gl_Position = vec4(clip * vec2(1, -1), 0, 1);
      }`;
    const fs = `#version 300 es
      precision mediump float;
      uniform vec4 u_color;
      out vec4 outColor;
      void main() { outColor = u_color; }`;

    const vert = this.compile(gl.VERTEX_SHADER, vs);
    const frag = this.compile(gl.FRAGMENT_SHADER, fs);
    if (!vert || !frag) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    this.program = program;

    this.buffer = gl.createBuffer();
    this.resize();
    window.addEventListener('resize', () => this.resize());
    this.draw();
    if (!this.reducedMotion) this.loop();
  }

  private compile(type: number, source: string): WebGLShader | null {
    const gl = this.gl;
    if (!gl) return null;
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return null;
    return shader;
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.floor(rect.height * dpr));
  }

  setStandings(scores: PlayerWeekScore[]): void {
    const max = Math.max(1, ...scores.map((s) => s.points));
    const palette: [number, number, number][] = [
      [0.918, 0.0, 0.851],
      [0.039, 0.741, 0.776],
      [0.443, 0.11, 0.569],
      [0.075, 0.243, 0.486],
      [0.039, 0.741, 0.776],
      [0.918, 0.0, 0.851],
      [0.443, 0.11, 0.569],
    ];
    this.bars = scores.map((s, i) => ({
      label: playerFirstName(s.playerId),
      value: s.points / max,
      color: palette[i % palette.length],
    }));
    this.draw();
  }

  burstPick(): void {
    if (this.reducedMotion) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    for (let i = 0; i < 24; i++) {
      const angle = (Math.PI * 2 * i) / 24;
      const speed = 2 + Math.random() * 3;
      this.particles.push({
        x: w * 0.5,
        y: h * 0.5,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
      });
    }
  }

  private loop = (): void => {
    this.draw();
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(): void {
    const gl = this.gl;
    const program = this.program;
    const buffer = this.buffer;
    if (!gl || !program || !buffer) return;

    const w = this.canvas.width;
    const h = this.canvas.height;
    gl.viewport(0, 0, w, h);
    gl.clearColor(0.035, 0.094, 0.2, 0.35);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(program);
    const resLoc = gl.getUniformLocation(program, 'u_resolution');
    const colorLoc = gl.getUniformLocation(program, 'u_color');
    const posLoc = gl.getAttribLocation(program, 'a_pos');
    gl.uniform2f(resLoc, w, h);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const pad = 12;
    const barH = Math.min(18, (h - pad * 2) / Math.max(1, this.bars.length) - 4);
    this.bars.forEach((bar, i) => {
      const y = pad + i * (barH + 4);
      const bw = Math.max(4, (w - pad * 2) * bar.value);
      this.rect(pad, y, bw, barH, bar.color, colorLoc);
    });

    if (!this.reducedMotion) {
      this.particles = this.particles.filter((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.life -= 0.02;
        if (p.life <= 0) return false;
        const size = 4 * p.life;
        this.rect(p.x, p.y, size, size, [0.918, 0.0, 0.851, p.life], colorLoc);
        return true;
      });
    }
  }

  private rect(
    x: number,
    y: number,
    w: number,
    h: number,
    color: [number, number, number] | [number, number, number, number],
    colorLoc: WebGLUniformLocation | null,
  ): void {
    const gl = this.gl;
    const buffer = this.buffer;
    if (!gl || !buffer || !colorLoc) return;
    const verts = new Float32Array([
      x, y,
      x + w, y,
      x, y + h,
      x + w, y,
      x + w, y + h,
      x, y + h,
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.DYNAMIC_DRAW);
    const alpha = color.length === 4 ? color[3] : 1;
    gl.uniform4f(colorLoc, color[0], color[1], color[2], alpha);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
  }
}
